import * as React from 'react'
import { ArrowLeft } from 'lucide-react'
import { Link } from 'react-router-dom'
import { StatusPill } from '../../components/primitives/StatusPill'
import { Button } from '../../components/ui/button'
import { NativeSelect, NativeSelectOption } from '../../components/ui/native-select'
import { META_TEXT } from '../IngestPage/shared/compact'
import type { TabularJobPill } from '../TabularIngestJobPage/tabularJobPill'
import type { SwitcherOption } from './util/configView'

/**
 * The sticky top bar every state of the page shares: back link, name, state
 * pill, the version switcher, a one-line meta, and the state's actions.
 */
export function ConfigSpine({
  name,
  backTo,
  backLabel = 'Back',
  pill,
  switcher,
  meta,
  actions,
  children
}: {
  name: string
  backTo: string
  backLabel?: string
  pill?: TabularJobPill
  switcher?: { value: string; options: SwitcherOption[]; onChange: (value: string) => void }
  meta?: string
  actions?: React.ReactNode
  /** A banner or note that belongs under the spine. */
  children?: React.ReactNode
}) {
  return (
    <div className="sticky top-12 z-[5] -mt-4 flex min-w-0 flex-col gap-1.5 border-b border-border bg-background pt-2 pb-2.5 md:-mt-6">
      <Button asChild variant="ghost" size="sm" className="-ml-2 h-6 w-fit px-1.5 text-11">
        <Link to={backTo}>
          <ArrowLeft className="size-3" />
          {backLabel}
        </Link>
      </Button>
      <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5">
        {pill ? <StatusPill status={pill.status}>{pill.label}</StatusPill> : null}
        <h1 className="min-w-0 flex-1 truncate text-base font-semibold" title={name}>
          {name}
        </h1>
        {switcher && switcher.options.length ? (
          <NativeSelect
            size="sm"
            aria-label="Version"
            data-testid="version-switcher"
            className="text-xs"
            value={switcher.value}
            onChange={(event) => switcher.onChange(event.target.value)}
          >
            {switcher.options.map((option) => (
              <NativeSelectOption key={option.value} value={option.value}>
                {option.label}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        ) : null}
        {actions ? <div className="flex flex-wrap items-center gap-1.5">{actions}</div> : null}
      </div>
      {meta ? (
        <p className={`${META_TEXT} truncate`} title={meta} data-testid="config-meta">
          {meta}
        </p>
      ) : null}
      {children}
    </div>
  )
}
